import React, { useState, useEffect } from 'react';
import { Users, CheckCircle, XCircle, Clock, Shield, RefreshCw, UserCheck, UserX, Trash2, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [resetRequests, setResetRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const [allUsersRes, pendingRes, resetRes] = await Promise.all([
        api.get('/admin/all-users'),
        api.get('/admin/pending-users'),
        api.get('/admin/password-reset-requests')
      ]);
      setUsers(allUsersRes.data || []);
      setPendingUsers(pendingRes.data || []);
      setResetRequests(resetRes.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users. Make sure you have admin access.');
    } finally {
      setLoading(false);
    }
  };

  const approveUser = async (userId) => {
    try {
      await api.post(`/admin/approve-user/${userId}`);
      toast.success('User approved successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to approve user');
    }
  };

  const rejectUser = async (userId) => {
    if (!window.confirm('Are you sure you want to reject and remove this user?')) return;
    try {
      await api.post(`/admin/reject-user/${userId}`);
      toast.success('User rejected and removed');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to reject user');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to permanently delete this user? This cannot be undone.')) return;
    try {
      await api.delete(`/admin/delete-user/${userId}`);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const resetPassword = async (userId, userName) => {
    const newPassword = window.prompt(`Enter new password for ${userName}:`);
    if (!newPassword) return;
    if (newPassword.length < 4) { toast.error('Password must be at least 4 characters'); return; }
    try {
      await api.post(`/admin/reset-password/${userId}`, { new_password: newPassword });
      toast.success(`Password reset for ${userName}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const approveResetRequest = async (requestId, fullName) => {
    const newPassword = window.prompt(`Enter new password for ${fullName}:`);
    if (!newPassword) return;
    if (newPassword.length < 4) { toast.error('Password must be at least 4 characters'); return; }
    try {
      await api.post(`/admin/approve-reset-request/${requestId}`, { new_password: newPassword });
      toast.success(`Password reset for ${fullName}`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve reset');
    }
  };

  const dismissResetRequest = async (requestId) => {
    if (!window.confirm('Dismiss this password reset request?')) return;
    try {
      await api.delete(`/admin/password-reset-requests/${requestId}`);
      toast.success('Request dismissed');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to dismiss request');
    }
  };

  const displayUsers = activeTab === 'pending' ? pendingUsers : activeTab === 'resets' ? [] : users;
  const approvedCount = users.filter(u => u.approved).length;
  const pendingCount = pendingUsers.length;
  const resetCount = resetRequests.length;

  return (
    <div className="animate-fade-in">
      <div className="relative bg-black/60 backdrop-blur-xl border-b border-white/5 overflow-hidden">
        <div className="hero-glow"></div>
        <div className="relative z-10 p-6 md:p-8 lg:p-12">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-5xl md:text-6xl font-bold uppercase tracking-tight leading-none text-white mb-2">
                User Management
              </h1>
              <p className="text-lg text-zinc-300">
                {approvedCount} approved users • {pendingCount} pending approval{resetCount > 0 ? ` • ${resetCount} reset requests` : ''}
              </p>
            </div>
            <button
              onClick={fetchUsers}
              disabled={loading}
              data-testid="refresh-users-btn"
              className="inline-flex items-center gap-2 bg-white/10 text-white hover:bg-white/20 border border-white/5 rounded-sm px-4 py-2 transition-all duration-200"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} strokeWidth={1.5} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="p-6 md:p-8 lg:p-12">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="glass-card p-4 flex items-center gap-4">
            <div className="p-3 bg-white/10 rounded-lg">
              <Users className="w-6 h-6 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{users.length}</p>
              <p className="text-sm text-zinc-400">Total Users</p>
            </div>
          </div>
          <div className="glass-card p-4 flex items-center gap-4">
            <div className="p-3 bg-green-500/20 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-400" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-400">{approvedCount}</p>
              <p className="text-sm text-zinc-400">Approved</p>
            </div>
          </div>
          <div className="glass-card p-4 flex items-center gap-4">
            <div className="p-3 bg-yellow-500/20 rounded-lg">
              <Clock className="w-6 h-6 text-yellow-400" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-2xl font-bold text-yellow-400">{pendingCount}</p>
              <p className="text-sm text-zinc-400">Pending Approval</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('all')}
            data-testid="tab-all-users"
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-sm font-bold uppercase tracking-wide text-sm transition-all duration-200 ${
              activeTab === 'all' 
                ? 'bg-white text-black' 
                : 'bg-white/10 text-white hover:bg-white/20'
            }`}
          >
            <Users className="w-4 h-4" strokeWidth={1.5} />
            All Users ({users.length})
          </button>
          <button
            onClick={() => setActiveTab('pending')}
            data-testid="tab-pending-users"
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-sm font-bold uppercase tracking-wide text-sm transition-all duration-200 ${
              activeTab === 'pending' 
                ? 'bg-white text-black' 
                : pendingCount > 0
                ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 hover:bg-yellow-500/30'
                : 'bg-white/10 text-white hover:bg-white/20'
            }`}
          >
            <Clock className={`w-4 h-4 ${pendingCount > 0 ? 'animate-pulse' : ''}`} strokeWidth={1.5} />
            Pending ({pendingCount})
          </button>
          <button
            onClick={() => setActiveTab('resets')}
            data-testid="tab-reset-requests"
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-sm font-bold uppercase tracking-wide text-sm transition-all duration-200 ${
              activeTab === 'resets' 
                ? 'bg-white text-black' 
                : resetCount > 0
                ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30 hover:bg-orange-500/30'
                : 'bg-white/10 text-white hover:bg-white/20'
            }`}
          >
            <KeyRound className={`w-4 h-4 ${resetCount > 0 ? 'animate-pulse' : ''}`} strokeWidth={1.5} />
            Reset Requests ({resetCount})
          </button>
        </div>

        {/* Content */}
        <div className="glass-card overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <RefreshCw className="w-12 h-12 text-white mx-auto mb-4 animate-spin" strokeWidth={1.5} />
              <p className="text-zinc-400">Loading users...</p>
            </div>
          ) : activeTab === 'resets' ? (
            resetRequests.length === 0 ? (
              <div className="p-12 text-center">
                <KeyRound className="w-12 h-12 text-zinc-600 mx-auto mb-4" strokeWidth={1.5} />
                <h2 className="text-xl font-bold text-white mb-2">No Reset Requests</h2>
                <p className="text-zinc-400">No players have requested a password reset.</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="border-b border-white/5">
                  <tr className="text-left">
                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-widest text-zinc-500">Player</th>
                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-widest text-zinc-500">Requested</th>
                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-widest text-zinc-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {resetRequests.map((req) => (
                    <tr key={req.id} className="hover:bg-white/5 transition-colors" data-testid={`reset-req-row-${req.id}`}>
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-bold text-white">{req.full_name}</p>
                          <p className="text-sm text-zinc-400">{req.email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-zinc-400">
                        {new Date(req.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => approveResetRequest(req.id, req.full_name)}
                            data-testid={`approve-reset-${req.id}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded text-xs font-bold uppercase transition-colors"
                          >
                            <CheckCircle className="w-3 h-3" />
                            Reset Password
                          </button>
                          <button
                            onClick={() => dismissResetRequest(req.id)}
                            data-testid={`dismiss-reset-${req.id}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded text-xs font-bold uppercase transition-colors"
                          >
                            <XCircle className="w-3 h-3" />
                            Dismiss
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          ) : displayUsers.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-12 h-12 text-zinc-600 mx-auto mb-4" strokeWidth={1.5} />
              <h2 className="text-xl font-bold text-white mb-2">
                {activeTab === 'pending' ? 'No Pending Users' : 'No Users Found'}
              </h2>
              <p className="text-zinc-400">
                {activeTab === 'pending' 
                  ? 'All users have been reviewed.' 
                  : 'No users have registered yet.'}
              </p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="border-b border-white/5">
                <tr className="text-left">
                  <th className="px-6 py-4 text-xs font-bold uppercase tracking-widest text-zinc-500">User</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase tracking-widest text-zinc-500">Role</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase tracking-widest text-zinc-500">Status</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase tracking-widest text-zinc-500">Registered</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase tracking-widest text-zinc-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {displayUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-white/5 transition-colors" data-testid={`user-row-${user.id}`}>
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-bold text-white">{user.full_name}</p>
                        <p className="text-sm text-zinc-400">{user.email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-bold uppercase ${
                        user.role === 'admin' 
                          ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' 
                          : 'bg-zinc-500/20 text-zinc-400 border border-zinc-500/30'
                      }`}>
                        {user.role === 'admin' && <Shield className="w-3 h-3" />}
                        {user.role || 'user'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {user.approved ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-bold uppercase bg-green-500/20 text-green-400 border border-green-500/30">
                          <CheckCircle className="w-3 h-3" />
                          Approved
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-bold uppercase bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                          <Clock className="w-3 h-3" />
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-zinc-400">
                      {new Date(user.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </td>
                    <td className="px-6 py-4">
                      {!user.approved && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => approveUser(user.id)}
                            data-testid={`approve-user-${user.id}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded text-xs font-bold uppercase transition-colors"
                          >
                            <UserCheck className="w-3 h-3" />
                            Approve
                          </button>
                          <button
                            onClick={() => rejectUser(user.id)}
                            data-testid={`reject-user-${user.id}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded text-xs font-bold uppercase transition-colors"
                          >
                            <UserX className="w-3 h-3" />
                            Reject
                          </button>
                        </div>
                      )}
                      {user.approved && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => resetPassword(user.id, user.full_name)}
                            data-testid={`reset-password-${user.id}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded text-xs font-bold uppercase transition-colors"
                          >
                            <KeyRound className="w-3 h-3" />
                            Reset Password
                          </button>
                          <button
                            onClick={() => deleteUser(user.id)}
                            data-testid={`delete-user-${user.id}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded text-xs font-bold uppercase transition-colors"
                          >
                            <Trash2 className="w-3 h-3" />
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
